import config from '../../config/config';
import { sessionAwareFetch } from './sessionAwareFetch';

export const accountService = {
  async getAccount() {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/`, {
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  async createAccount(userData) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      let body = {};
      try {
        body = await response.json();
      } catch {
        /* ignore */
      }
      const err = new Error(
        body.error || `HTTP error! status: ${response.status}`,
      );
      err.status = response.status;
      if (body.code) err.code = body.code;
      throw err;
    }
    return await response.json();
  },

  async listAccountsForAdmin() {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/account-api/Accounts`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        },
      },
    );

    if (!response.ok) {
      let body = {};
      try {
        body = await response.json();
      } catch {
        /* ignore */
      }
      throw new Error(body.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  },

  async assignAccountToBusiness(accountId, businessId) {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/account-api/Accounts/${encodeURIComponent(accountId)}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        },
        body: JSON.stringify({
          BusinessId: businessId == null ? '' : businessId,
        }),
      },
    );

    if (!response.ok) {
      let body = {};
      try {
        body = await response.json();
      } catch {
        /* ignore */
      }
      throw new Error(body.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  async updateAccount(updatedAccount) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      credentials: 'include',
      body: JSON.stringify(updatedAccount),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  },

  async deleteAccount() {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/`, {
      method: 'DELETE',
      headers: {
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      credentials: 'include',
    });

    if (response.status === 204) {
      return;
    }

    let body = {};
    try {
      body = await response.json();
    } catch {
      /* ignore */
    }
    const err = new Error(
      body.error || `HTTP error! status: ${response.status}`,
    );
    err.status = response.status;
    throw err;
  },

  async getSavedBusinesses(
    includeBusinessDetails = false,
    includeServices = false,
  ) {
    const params = new URLSearchParams();
    if (includeBusinessDetails) {
      params.set('includeBusinessDetails', includeBusinessDetails);
    }
    if (includeServices) {
      params.set('includeServices', includeServices);
    }

    const response = await sessionAwareFetch(
      `${config.apimUrl}/account-api/Saved-Businesses?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error('Failed to get saved providers');
    }

    return await response.json();
  },

  async editSavedBusinesses(businessIds) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/Saved-Businesses`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      credentials: 'include',
      body: JSON.stringify(businessIds),
    });

    if (!response.ok) {
      throw new Error('Failed to edit saved businesses');
    }

    return await response.json();
  },

  /** Authenticated resend; POST with no body (matches Email/Resend-Token). */
  async resendVerificationEmail() {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/account-api/Email/Resend-Token`,
      {
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const err = new Error(`Resend verification failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }

    return response.status;
  },

  async verifyEmail(email, token) {
    const body = {
      Email: decodeURIComponent(email).trim().toLowerCase(),
      Token: token,
    };

    const response = await sessionAwareFetch(
      `${config.apimUrl}/account-api/Email/Verify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        },
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw response.status;
    }

    // If successful, you can just return the status.
    return response.status;
  },

  async sendPasswordResetLink(email) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/Password/Forgot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      body: JSON.stringify({ Email: email }),
    });

    return response;
  },

  async resetPassword({ Token, NewPassword }) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/Password/Reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
      },
      body: JSON.stringify({ Token, NewPassword }),
    });

    return response;
  },
  async changePassword(currentPassword, newPassword) {
    const response = await sessionAwareFetch(`${config.apimUrl}/account-api/Password/Change`, {
      method: 'POST',
      headers: {
        'Ocp-Apim-Subscription-Key': config.apimKeyAccountService,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ExistingPassword: currentPassword,
        NewPassword: newPassword,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return;
  },
};
